import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Button, Snackbar, Typography } from "@dealer-center/core-components";
import {
  type PresignedUrlResponse,
  getPresignedUrl
} from "@dealer-center/ddc-pages-accountassets-fetch";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import type { decodedJwtAndPermissions } from "@dealer-center/ddc-api-user-interface";
import { deriveEnvironmentName } from "@dealer-center/core-enviroment";
import "./GetPresigned.scss";
import { Delete } from "./Delete";
import { Locale } from "@dealer-center/ddc-core-utils";
import ConvertFileToImageType from "./ConvertFileToImageType";
import {
  fileMimeType,
  pathFileTypes
} from "@dealer-center/ddc-pages-accountassets-interface";

// these are from the User Story US1783992, specified for dealerphoto, using for companylogo as well
const maxImageWidth = 1600;
const maxImageHeight = 900;

const supportedFileTypes = ["jpg", "jpeg", "png", "gif", "webp"];

export const GetPresigned = ({
  path
}: {
  path: string;
}): React.ReactElement => {
  const { ccUserInfo } = useOutletContext<{
    ccUserInfo: ControlcenterAPIInterfaceExternal;
    userEntity: decodedJwtAndPermissions;
  }>();

  const fileType = pathFileTypes[path];
  const mimeType = fileMimeType(fileType);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedBlob, setSelectedBlob] = useState<Blob | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [presignedUrlResponse, setPresignedUrlResponse] =
    useState<PresignedUrlResponse | null>(null);
  const [uploadStatus, setUploadStatus] = useState<{
    type: "success" | "error";
    message: string;
    timestamp: number;
    metadata?: {
      createdBy: string;
      createdOn: string;
    };
    publicUrl?: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [clearDeleteResults, setClearDeleteResults] = useState(0);
  const [alertState, setAlertState] = useState<{
    type: string;
    message: string;
  } | null>(null);

  const handleUploadStarted = useCallback(() => {
    setClearDeleteResults(prev => prev + 1);
  }, []);

  const handleAlert = useCallback((type: string, message: string) => {
    setAlertState({ type, message });
  }, []);

  useEffect(() => {
    if (uploadStatus?.type === "error") {
      handleAlert("error", uploadStatus.message);
    }
  }, [uploadStatus]);

  const onDragEvent = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelection = (file: File | null) => {
    if (!file) return;

    const extn = file?.name?.toLowerCase()?.split(".")?.pop();
    if (!extn || !supportedFileTypes.includes(extn)) {
      handleAlert(
        "error",
        Locale.getText("UNSUPPORTED_BROWSER_IMAGE") as string
      );
      return;
    }

    setSelectedFile(file);
    setSelectedBlob(null);
    setPresignedUrlResponse(null);
    setUploadStatus(null);
  };

  const onDropEvent = (event: React.DragEvent<HTMLDivElement>) => {
    onDragEvent(event);
    const file = event.dataTransfer.files?.[0];
    handleFileSelection(file || null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    handleFileSelection(file || null);
  };

  const uploadBlob = async (presignedUrlResponse, blob: Blob) => {
    try {
      if (presignedUrlResponse) {
        const uploadResponse = await fetch(presignedUrlResponse.url, {
          method: "PUT",
          headers: {
            "Content-Type": blob.type || mimeType
          },
          body: blob
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.statusText}`);
        }

        // Construct the public URL based on environment with cache-busting parameter
        const environment = deriveEnvironmentName();
        const baseUrl =
          environment === "prod"
            ? "https://ddc-account-assets.ddcplatform.dealer.com"
            : "https://ddc-account-assets.ddcplatform-np.dealer.com";
        const timestamp = new Date().getTime();
        const publicUrlWithCache = `${baseUrl}/${path}/${ccUserInfo.accountId}.${fileType}?t=${timestamp}`;

        setUploadStatus({
          type: "success",
          message: `Successfully uploaded ${selectedFile?.name} as ${path}/${ccUserInfo.accountId}.${fileType}`,
          timestamp: Date.now(),
          metadata: presignedUrlResponse.metadata,
          publicUrl: publicUrlWithCache
        });
      }
    } catch (err) {
      setUploadStatus({
        type: "error",
        message: err instanceof Error ? err.message : "Failed to upload file",
        timestamp: Date.now()
      });
    } finally {
      setSelectedFile(null);
      setSelectedBlob(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setIsLoading(false);
    }
  };

  const handleUploadImage = async () => {
    if (!selectedBlob || !ccUserInfo?.accountId || !ccUserInfo?.userId) {
      return;
    }

    // Notify parent to clear delete results
    handleUploadStarted();

    setIsLoading(true);
    setUploadStatus(null);
    setPresignedUrlResponse(null);

    const userId = ccUserInfo.internalUserId || ccUserInfo.userId;

    const response = await getPresignedUrl({
      asAccount: ccUserInfo.accountId,
      asUser: userId,
      path: path,
      locale: "en_US"
    });

    setPresignedUrlResponse(response);

    await uploadBlob(response, selectedBlob);
  };

  const canGenerate: boolean =
    !!selectedBlob && !!ccUserInfo?.accountId && !!ccUserInfo?.userId;

  const converterCanvas = () => {
    if (selectedFile) {
      return (
        <ConvertFileToImageType
          maxImageHeight={maxImageHeight}
          maxImageWidth={maxImageWidth}
          selectedFile={selectedFile}
          targetImageType={fileType}
          onConversion={setSelectedBlob}
        />
      );
    }
    return null;
  };

  const renderDropZone = () => {
    if (canGenerate) {
      return (
        <div className="selected-file-info">
          <Typography sx={{ mt: 1, color: "success.main" }} variant="body-sm">
            {Locale.getText("IMAGE") as string}: {selectedFile?.name || ""} (
            {((selectedFile?.size || 0) / 1024).toFixed(2)} KB)
          </Typography>
          <div className="button-block">
            <Button
              analyticsConfiguration={{
                eventName: "PagesAccountassetsUi Clear Selected Photo"
              }}
              buttonStyle="secondary"
              disabled={isLoading}
              onClick={() => {
                setSelectedFile(null);
                setSelectedBlob(null);
              }}
            >
              {Locale.getText("CLEAR") as string}
            </Button>
            <Button
              analyticsConfiguration={{
                eventName: "PagesAccountassetsUi Upload Dealer Photo"
              }}
              buttonStyle="primary"
              disabled={!canGenerate}
              isLoading={isLoading}
              onClick={handleUploadImage}
            >
              {Locale.getText("UPLOAD") as string}
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div
        className="account-asset-dropzone"
        onDragEnter={onDragEvent}
        onDragOver={onDragEvent}
        onDrop={onDropEvent}
      >
        <div className="input-container">
          <p>
            {Locale.getText("DRAG_AN_IMAGE")}
            <span> {Locale.getText("OR")}</span>
          </p>
          <input
            accept={`${supportedFileTypes.map(x => "." + x).join(",")},image/*`}
            id="files"
            name="file"
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="files">
            <Button
              analyticsConfiguration={{
                eventName: "PagesAccountassetsUi Upload Browse Files"
              }}
              buttonStyle="tertiary"
              className="files-browse"
              onClick={triggerFileInput}
            >
              {Locale.getText("BROWSE")}
            </Button>
          </label>
        </div>
        <Delete
          clearTrigger={clearDeleteResults}
          handleAlert={handleAlert}
          path={path}
        />
      </div>
    );
  };

  return (
    <>
      {converterCanvas()}
      <div className="get-presigned-container">
        {renderDropZone()}{" "}
        {alertState && (
          <Snackbar
            className="alert-modal"
            message={alertState.message}
            position="bottom-center"
            show={true}
            type={alertState.type === "error" ? "error" : "success"}
            onClose={() => setAlertState(null)}
          />
        )}
      </div>
    </>
  );
};
